import {
  addressType,
  deliveryLocation,
  paymentType,
} from "../../utils/constants";
import Button from "../common/Button";
import Input from "../common/Input";

const BillingDetail = ({
  setFieldValue,
  values,
  handleChange,
  billingData,
  setBillingData,
  readOnly,
}) => {
  const handleAddressChange = (value) => {
    const deliveryTypes = ["isClinic", "isCollect", "isAddress"];
    deliveryTypes.forEach((type) => {
      setFieldValue(`delivery.${type}`, type === value);
      setBillingData(0);
    });
  };

  // Get selected payment name
  const selectedPaymentName = paymentType?.find(
    (item) => item.value === values?.payment
  )?.name || "Select Payment Type";

  // Get selected delivery name
  const selectedDeliveryName = deliveryLocation?.find(
    (item) => item.value === Object.keys(values?.delivery || {}).find(
      (key) => values.delivery[key] === true && ["isClinic", "isCollect", "isAddress"].includes(key)
    )
  )?.name || "Select Delivery Type";

  const {
    subTotal,
    totalDiscount,
    handlingFee,
    smallCartFee,
    payableAmount,
    deliveryCharges,
  } = values?.billing;

  const billingDropdowns = (
    <div className={`w-full flex flex-col ${values?.delivery?.isAddress ? "lg:flex-row" : ""} gap-2 transition-all duration-300`}>
      {/* Select Payment Dropdown */}
      <div className="flex-1 w-full">
        <div className="relative">
          <div
            role="button"
            tabIndex={0}
            onClick={(e) => {
              if (readOnly) return;
              const currentTarget = e.currentTarget;
              const dropdown = currentTarget.nextElementSibling;
              if (dropdown) {
                dropdown.style.display = dropdown.style.display === "block" ? "none" : "block";
              }
            }}
            onBlur={(e) => {
              if (readOnly) return;
              const currentTarget = e.currentTarget;
              setTimeout(() => {
                if (currentTarget) {
                  const dropdown = currentTarget.nextElementSibling;
                  if (dropdown) dropdown.style.display = "none";
                }
              }, 200);
            }}
            className={`w-full h-[44px] px-4 py-2 border border-primary-100 rounded-[8px] bg-primary-50 text-primary-700 font-semibold text-sm outline-none focus:ring-1 focus:ring-primary-600 flex items-center transition-all hover:bg-primary-50 shadow-sm ${readOnly ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
          >
            <span className="truncate flex-1">{selectedPaymentName}</span>
            <span className="text-[10px] text-primary-600 font-normal whitespace-nowrap ml-1 bg-primary-50 px-2 py-0.5 rounded-full">(Change Payment)</span>
          </div>
          <div className="payment-dropdown absolute top-full left-0 right-0 mt-2 bg-white border border-primary-700 rounded-[8px] max-h-[250px] overflow-y-auto z-[100] hidden shadow-2xl animate-in fade-in slide-in-from-top-2">
            {paymentType?.map((option) => (
              <div
                key={option.value}
                onMouseDown={() => {
                  setFieldValue("payment", option.value);
                  setBillingData(0);
                }}
                className="px-4 py-3 hover:bg-primary-100 cursor-pointer text-primary-700 font-medium text-sm transition-colors border-b border-primary-50 last:border-b-0"
              >
                {option.name}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Select Delivery Dropdown */}
      <div className="flex-1 w-full">
        <div className="relative">
          <div
            role="button"
            tabIndex={0}
            onClick={(e) => {
              if (readOnly) return;
              const currentTarget = e.currentTarget;
              const dropdown = currentTarget.nextElementSibling;
              if (dropdown) {
                dropdown.style.display = dropdown.style.display === "block" ? "none" : "block";
              }
            }}
            onBlur={(e) => {
              if (readOnly) return;
              const currentTarget = e.currentTarget;
              setTimeout(() => {
                if (currentTarget) {
                  const dropdown = currentTarget.nextElementSibling;
                  if (dropdown) dropdown.style.display = "none";
                }
              }, 200);
            }}
            className={`w-full h-[44px] px-4 py-2 border border-primary-100 rounded-[8px] bg-primary-50 text-primary-700 font-semibold text-sm outline-none focus:ring-1 focus:ring-primary-600 flex items-center transition-all hover:bg-primary-50 shadow-sm ${readOnly ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
          >
            <span className="truncate flex-1">{selectedDeliveryName}</span>
            <span className="text-[10px] text-primary-600 font-normal whitespace-nowrap ml-1 bg-primary-50 px-2 py-0.5 rounded-full">(Change Delivery)</span>
          </div>
          <div className="delivery-dropdown absolute top-full left-0 right-0 mt-2 bg-white border border-primary-700 rounded-[8px] max-h-[250px] overflow-y-auto z-[100] hidden shadow-2xl animate-in fade-in slide-in-from-top-2">
            {deliveryLocation?.map((option) => (
              <div
                key={option.value}
                onMouseDown={() => {
                  handleAddressChange(option.value);
                }}
                className="px-4 py-3 hover:bg-primary-100 cursor-pointer text-primary-700 font-medium text-sm transition-colors border-b border-primary-50 last:border-b-0 flex items-center gap-3"
              >
                <span className="shrink-0 scale-110">{option.icon}</span>
                <span>{option.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-full max-lg:mb-3 lg:w-[calc(100%)] flex flex-col lg:flex-row gap-2">
      <div className={`w-full lg:flex-1 min-w-0 transition-all duration-300 ${!values?.delivery?.isAddress ? "bg-white lg:max-w-[200px]" : "bg-white rounded-xl shadow-sm border border-primary-100 p-3"}`}>
        <div className="flex flex-col">
          {!values?.delivery?.isAddress && (
            <div className="">
              {billingDropdowns}
            </div>
          )}

          <div className="w-full">
            {values?.delivery?.isAddress && (
              <div>
                <div className="flex items-center flex-wrap gap-3 mb-2">
                  <div className="w-full flex text-[13px] items-center justify-left gap-2 text-secondry-600 bg-gray-100 px-4 py-2 rounded-[8px]">
                    <h3 className="flex-1 text-primary-700 font-medium">Delivery Address</h3>
                    {addressType?.map(({ name, value, index }) => (
                      <Button
                        key={index}
                        type="button"
                        disabled={readOnly}
                        className={`inline-block  flex text-[13px] items-center justify-center 
                        gap-2 text-secondry-600 focus:border-primary-200 border h-[36px] py-[5px] px-[20px] 
                        w-auto max-w-[100px] lg:max-w-auto xl:max-w-auto rounded-full shadow-none outline-none 
                        ${values?.delivery?.address?.addressType === value
                            ? "bg-primary-300 border-primary-600"
                            : "bg-primary-50 border-primary-600"
                          } ${readOnly ? 'opacity-70 cursor-not-allowed' : ''}`}
                        onClick={() => {
                          if (!readOnly) setFieldValue("delivery.address.addressType", value);
                        }}
                      >
                        {name}
                      </Button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-12 gap-[8px]">
                  <div className="col-span-6 lg:col-span-4">
                    <Input
                      placeholder="Enter"
                      inputBox="mb-0"
                      type="text"
                      labelName="Premises No.*"
                      labelClassName="block text-primary-400 text-[13px] leading-[20px]"
                      className="border-none bg-transparent shadow-none outline-none placeholder:text-secondry-600 w-full h-[24px]"
                      name={`delivery.address.premisesNoFloor`}
                      value={values?.delivery?.address?.premisesNoFloor || ""}
                      readOnly={readOnly}
                      onChange={(e) => {
                        if (!readOnly) {
                          setBillingData(0);
                          handleChange(e);
                        }
                      }}
                    />
                  </div>
                  <div className="col-span-6 lg:col-span-4">
                    <Input
                      placeholder="Enter"
                      inputBox="mb-0"
                      type="text"
                      labelName="Premises Name*"
                      labelClassName="block text-primary-400 text-[13px] leading-[20px]"
                      className="border-none bg-transparent shadow-none outline-none placeholder:text-secondry-600 w-full h-[24px]"
                      name={`delivery.address.premisesName`}
                      value={values?.delivery?.address?.premisesName || ""}
                      readOnly={readOnly}
                      onChange={(e) => {
                        if (!readOnly) {
                          setBillingData(0);
                          handleChange(e);
                        }
                      }}
                    />
                  </div>
                  <div className="col-span-6 lg:col-span-4">
                    <Input
                      placeholder="Enter"
                      inputBox="mb-0"
                      type="text"
                      labelName="Landmark"
                      labelClassName="block text-primary-400 text-[13px] leading-[20px]"
                      className="border-none bg-transparent shadow-none outline-none placeholder:text-secondry-600 w-full h-[24px]"
                      name={`delivery.address.landmark`}
                      value={values?.delivery?.address?.landmark || ""}
                      readOnly={readOnly}
                      onChange={(e) => {
                        if (!readOnly) {
                          setBillingData(0);
                          handleChange(e);
                        }
                      }}
                    />
                  </div>
                  <div className="col-span-6 lg:col-span-4">
                    <Input
                      placeholder="Enter"
                      inputBox="mb-0"
                      type="text"
                      labelName="Area/Road*"
                      labelClassName="block text-primary-400 text-[13px] leading-[20px]"
                      className="border-none bg-transparent shadow-none outline-none placeholder:text-secondry-600 w-full h-[24px]"
                      name={`delivery.address.areaRoad`}
                      value={values?.delivery?.address?.areaRoad || ""}
                      readOnly={readOnly}
                      onChange={(e) => {
                        if (!readOnly) {
                          setBillingData(0);
                          handleChange(e);
                        }
                      }}
                    />
                  </div>
                  <div className="col-span-6 lg:col-span-4">
                    <Input
                      placeholder="Enter"
                      inputBox="mb-0"
                      type="text"
                      labelName="City*"
                      labelClassName="block text-primary-400 text-[13px] leading-[20px]"
                      className="border-none bg-transparent shadow-none outline-none placeholder:text-secondry-600 w-full h-[24px]"
                      name={`delivery.address.city`}
                      value={values?.delivery?.address?.city || ""}
                      readOnly={readOnly}
                      onChange={(e) => {
                        if (!readOnly) {
                          setBillingData(0);
                          handleChange(e);
                        }
                      }}
                    />
                  </div>
                  <div className="col-span-6 lg:col-span-4">
                    <Input
                      placeholder="Enter"
                      inputBox="mb-0"
                      type="text"
                      labelName="Pin Code*"
                      labelClassName="block text-primary-400 text-[13px] leading-[20px]"
                      className="border-none bg-transparent shadow-none outline-none placeholder:text-secondry-600 w-full h-[24px]"
                      name={`delivery.address.pincode`}
                      value={values?.delivery?.address?.pincode || ""}
                      readOnly={readOnly}
                      onChange={(e) => {
                        if (!readOnly) {
                          setBillingData(0);
                          handleChange(e);
                        }
                      }}
                    />
                  </div>
                  <div className="col-span-6 lg:col-span-6">
                    <Input
                      placeholder="Enter"
                      inputBox="mb-0"
                      type="text"
                      labelName="State*"
                      labelClassName="block text-primary-400 text-[13px] leading-[20px]"
                      className="border-none bg-transparent shadow-none outline-none placeholder:text-secondry-600 w-full h-[24px]"
                      name={`delivery.address.state`}
                      value={values?.delivery?.address?.state || ""}
                      readOnly={readOnly}
                      onChange={(e) => {
                        if (!readOnly) {
                          setBillingData(0);
                          handleChange(e);
                        }
                      }}
                    />
                  </div>
                  <div className="col-span-6 lg:col-span-6">
                    <Input
                      placeholder="Enter"
                      inputBox="mb-0"
                      type="text"
                      labelName="Alternate Phone Number"
                      labelClassName="block text-primary-400 text-[13px] leading-[20px]"
                      className="border-none bg-transparent shadow-none outline-none placeholder:text-secondry-600 w-full h-[24px]"
                      name={`delivery.address.phoneNumber2`}
                      value={values?.delivery?.address?.phoneNumber2 || ""}
                      readOnly={readOnly}
                      onChange={(e) => {
                        if (!readOnly) {
                          setBillingData(0);
                          handleChange(e);
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="bg-primary-50 p-3 rounded-[8px] w-full lg:flex-1 min-w-0 flex flex-col gap-3 transition-all duration-300">
        {values?.delivery?.isAddress && (
          <div className="w-full">
            {billingDropdowns}
          </div>
        )}
        <div className="flex flex-col justify-center h-full">
          <h4 className="text-primary-700 font-bold text-[16px] flex items-center gap-2">
            ~Net Amount: ₹{payableAmount}
            <span className="text-gray-500 text-[13px] font-semibold flex items-center gap-1">
              (₹{subTotal})
            </span>
            <span className="text-success-600 flex items-center gap-1">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                <line x1="7" y1="7" x2="7.01" y2="7" />
              </svg>
              ₹{totalDiscount} off
            </span>
            {values?.billing?.totalReturnAmount > 0 && (
              <span className="text-rose-700 text-[12px] font-extrabold flex items-center gap-1 bg-rose-50 px-1.5 py-0.5 rounded border border-rose-200">
                -₹{values.billing.totalReturnAmount} return
              </span>
            )}
          </h4>
          {(handlingFee > 0 || smallCartFee > 0 || deliveryCharges > 0) && (
            <p className="text-primary-400 font-medium text-[12px] flex flex-wrap gap-x-3 gap-y-1 mt-1">
              {handlingFee > 0 && <span>• Handling: ₹{handlingFee}</span>}
              {smallCartFee > 0 && <span>• Small Cart: ₹{smallCartFee}</span>}
              {deliveryCharges > 0 && <span>• Delivery: ₹{deliveryCharges}</span>}
            </p>
          )}
          <span className="text-primary-400 font-light text-[10px] block text-left mt-0.5">
            "~Net Amount" is an estimated billing amount. Final billing will be confirmed at the 'Packed' stage of 'Tracking'.
          </span>
        </div>
      </div>
    </div>
  );
};

export default BillingDetail;
